import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaBriefcase, FaCalendar, FaMapMarkerAlt, FaCode, FaDatabase, FaCloud, FaDesktop, FaTools } from 'react-icons/fa';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Experiencepage = () => {
  const containerRef = useRef(null);

  const experiences = [
    {
      id: 1,
      company: "Addis Ababa University",
      position: "Full Stack Developer — Academic & Personal Projects",
      duration: "Sep 2023 – Present",
      location: "Onsite",
      tasks: [
        "Developed and deployed multiple full-stack applications using React, Node.js, and MongoDB",
        "Boosted development efficiency by 25% through component-based architecture and reusable utilities",
        "Designed secure RESTful APIs and implemented error handling, validation, and authentication",
        "Created interactive platforms to support student career development and project-based learning"
      ],
      tools: ["React", "Node.js", "MongoDB", "TypeScript", "Next.js"]
    },
    {
      id: 2,
      company: "Nile Technologies",
      position: "Full Stack Developer Intern",
      duration: "Mar 2025 – Jun 2025",
      location: "Remote",
      tasks: [
        "Engineered a full-featured School Management System using MERN stack with modular and maintainable architecture",
        "Integrated WebSocket for real-time communication and implemented Role-Based Access Control (RBAC)",
        "Collaborated in Agile teams to implement secure authentication using JSON Web Tokens (JWT)"
      ],
      tools: ["React", "Node.js", "MongoDB", "WebSocket", "JWT", "MERN Stack"]
    },
    {
      id: 3,
      company: "Sydek",
      position: "Software Developer",
      duration: "Jun 2025 – Present",
      location: "Remote",
      tasks: [
        "Spearheaded development of a cross-platform campaign and fundraising app using Flutter with clean DDD architecture",
        "Designed and implemented a Node.js + TypeScript backend with RESTful APIs to handle campaigns, donations, and user management",
        "Integrated secure authentication and authorization using JWT with role-based access (Organizer, Donor, Admin)",
        "Enabled real-time campaign updates and notifications using WebSocket and Firebase Cloud Messaging (FCM)",
        "Optimized app performance and state management using Riverpod and modular architecture",
        "Collaborated in Agile sprints, contributing to UI/UX decisions and end-to-end feature delivery across mobile and backend"
      ],
      tools: ["Flutter", "Node.js", "TypeScript", "REST API", "WebSocket", "Firebase", "Riverpod", "DDD"]
    },
    {
      id: 4,
      company: "SWENETIX TECH",
      position: "Software Engineer Intern",
      duration: "Jun 2025 – Oct 2025",
      location: "Remote",
      tasks: [
        "Contributed to a large-scale full-stack system covering frontend, backend, and API development",
        "Focused on improving performance, enhancing responsiveness, and optimizing system efficiency",
        "Collaborated effectively with teammates through agile workflows to ensure timely and high-quality delivery of project milestones"
      ],
      tools: ["MERN Stack", "ERP", "Docker", "Node.js", "React", "MongoDB"]
    }
  ];
  

  useEffect(() => {
    const cards = containerRef.current?.querySelectorAll('.experience-card');
    
    cards?.forEach((card, index) => {
      gsap.fromTo(card,
        {
          opacity: 0,
          y: 50
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          delay: index * 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <section id="experience" className="min-h-screen bg-gray-900 py-20 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-32 h-32 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Work
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"> Experience</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            My professional journey and the tools I've used to deliver results
          </p>
        </motion.div>

        {/* Experience List */}
        <div ref={containerRef} className="space-y-8">
          {experiences.map((experience, index) => (
            <motion.div
              key={experience.id}
              className="experience-card bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 shadow-xl hover:shadow-blue-500/10 transition-all duration-300"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">{experience.position}</h3>
                  <p className="text-xl text-blue-400 font-semibold">{experience.company}</p>
                </div>
                <div className="flex items-center space-x-6 mt-4 md:mt-0 text-gray-300">
                  <div className="flex items-center space-x-2">
                    <FaCalendar className="text-blue-400" />
                    <span>{experience.duration}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaMapMarkerAlt className="text-blue-400" />
                    <span>{experience.location}</span>
                  </div>
                </div>
              </div>

              {/* Tasks Section */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <FaBriefcase className="mr-2 text-blue-400" />
                  Key Tasks & Responsibilities
                </h4>
                <ul className="space-y-3">
                  {experience.tasks.map((task, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                      className="flex items-start space-x-3 text-gray-300"
                    >
                      <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></span>
                      <span>{task}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* Tools Section */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <FaTools className="mr-2 text-blue-400" />
                  Tools & Technologies Used
                </h4>
                <div className="flex flex-wrap gap-3">
                  {experience.tools.map((tool, idx) => (
                    <motion.span
                      key={idx}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: idx * 0.05 }}
                      className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-sm border border-blue-500/20 hover:bg-blue-500/20 transition-all duration-300"
                    >
                      {tool}
                    </motion.span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary Stats */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6"
        >
          {[
            { number: "2+", label: "Years Experience", icon: FaBriefcase },
            { number: "10+", label: "Projects Completed", icon: FaCode },
            { number: "15+", label: "Technologies", icon: FaTools },
            { number: "90%", label: "Client Satisfaction", icon: FaDesktop }
          ].map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="text-center p-6 bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-700/50"
            >
              <stat.icon className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">{stat.number}</div>
              <div className="text-gray-300">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Experiencepage; 